"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.RoutePostStream = exports.RoutePostForUseMutator = exports.RoutePostForUseModel = exports.RoutePost = exports.RouteGet = exports.Route = void 0;
var _fetchJson = require("../fetchJson");
var _modelStore = require("./modelStore");
/** 
 * Selects all keys in U whose values extend T.
 */

/**
 * Represents a generic route. Cannot be used directly, use one of the derived classes instead.
 */
class Route {
  constructor(method) {
    this.method = method;
    if (new.target === Route) {
      throw new Error("Cannot instantiate Route directly. Please use one of the derived classes.");
    }
  }
  execute(request) {
    throw new Error('Cannot call execute on an instance of Route. Please use RouteGet or RoutePost. URL:' + this.url + ' Method:' + this.method + ' Request:' + JSON.stringify(request));
  }
}

/**
 * Represents a route that uses the POST method.
 */
exports.Route = Route;
class RoutePost extends Route {
  constructor() {
    super('POST');
  }
  execute(request) {
    if (!this.url) throw new Error('Please set the `url` property of this route before calling `execute`.');
    return (0, _fetchJson.fetchPost)(this.url, request);
  }
}

/**
 * Represents a route that uses the GET method.
 */
exports.RoutePost = RoutePost;
class RouteGet extends Route {
  constructor() {
    super('GET');
  }
  execute(request) {
    if (!this.url) throw new Error('Please set the `url` property of this route before calling `execute`.');
    return (0, _fetchJson.fetchGet)(this.url, request);
  }
}

/**
 * Represents a route that uses the POST method and streams updates.
 */
exports.RouteGet = RouteGet;
class RoutePostStream extends RoutePost {
  constructor() {
    super();
  }
  executeWithUpdates(request, onUpdate) {
    if (!this.url) throw new Error('Please set the `url` property of this route before calling `execute`.');
    return (0, _fetchJson.fetchPostStream)(this.url, request, onUpdate);
  }
}

/**
 * Represents a route that can provide data for useModel.
 */
exports.RoutePostStream = RoutePostStream;
class RoutePostForUseModel extends RoutePost {
  constructor(params) {
    super();
    this.model = params.model;
    this.filter = params.filter;
    this.tryFilter = !!params.tryFilter;
    this.key = params.key;
    if (this.tryFilter && this.key) {
      throw new Error('Cannot use tryFilter and key at the same time. Please see the jsdoc for the RouteForUseModel constructor for more information.');
    }
  }
  async execute(request) {
    if (!this.url) throw new Error('Please set the `url` property of this route before calling `execute`.');
    let response = await (0, _fetchJson.fetchPost)(this.url, request);
    if (!isV3ResponseForUseModel(response, this.model)) {
      const validationError = validateV3ResponseForUseModel(response, this.model);
      throw new Error('Response did not match expected type: ' + validationError);
    }
    return response;
  }
}

/**
 * Represents a route that can mutate data for useMutator.
 */
exports.RoutePostForUseModel = RoutePostForUseModel;
class RoutePostForUseMutator extends RoutePost {
  constructor() {
    super();
  }
  async execute(request) {
    if (!this.url) throw new Error('Please set the `url` property of this route before calling `execute`.');
    let response = await (0, _fetchJson.fetchPost)(this.url, request);
    if (!isV3Response(response)) {
      const validationError = validateV3Response(response);
      throw new Error('Response did not match expected type: ' + validationError);
    }
    return response;
  }
}

/**
 * Returns whether the response is a valid V3ResponseForUseModel of the given model.
 * @param response The response to validate.
 * @param expectedModel The model that the response should be for.
 * @returns Whether the response is a valid V3ResponseForUseModel of the given model.
 */
exports.RoutePostForUseMutator = RoutePostForUseMutator;
function isV3ResponseForUseModel(response, expectedModel) {
  return validateV3ResponseForUseModel(response, expectedModel) === undefined;
}

/**
 * Validates whether the response is a valid V3ResponseForUseModel of the given model.
 * @param response The response to validate.
 * @param expectedModel The model that the response should be for.
 * @returns `undefined` if the response is valid, or an error message if not.
 */
function validateV3ResponseForUseModel(response, expectedModel) {
  if (!response) return 'response is not an object.';
  if (typeof response !== 'object') return 'response is not an object.';
  if (!('success' in response)) return 'response does not have a `success` property.';
  if (!('result' in response)) return 'response does not have a `result` property.';
  const {
    success,
    result
  } = response;
  if (success !== true) return 'response.success is not true.';
  if (!Array.isArray(result)) return 'response.result is not an array.';
  if (result.length !== 1) return 'response.result does not have exactly one item.';
  const resultItem = result[0];
  if (!resultItem) return 'response.result[0] is not an object.';
  if (typeof resultItem !== 'object') return 'response.result[0] is not an object.';
  if (!('operation' in resultItem)) return 'response.result[0] does not have an `operation` property.';
  if (!('data' in resultItem)) return 'response.result[0] does not have a `data` property.';
  if (!('model' in resultItem)) return 'response.result[0] does not have a `model` property.';
  const {
    operation,
    data,
    model
  } = resultItem;
  if (operation !== 'get') return 'response.result[0].operation is not `get`.';
  if (model !== expectedModel) return `response.result[0].model is not \`${expectedModel}\`.`;
  if (!Array.isArray(data)) return 'response.result[0].data is not an array.';
  if (data.length > 0) {
    let primaryKey = (0, _modelStore.getModelPrimaryKey)(expectedModel);
    if (!(primaryKey in data[0])) return `response.result[0].data[0] does not have a \`${primaryKey}\` property, which is the primary key for model \`${expectedModel}\`.`;
  }

  // Going to stop here, but data should be an array of the expected model type.
  return;
}

/**
 * Returns whether the response is a valid V3Response.
 * @param response The response to validate.
 * @param expectedModel The model that the response should be for.
 * @returns Whether the response is a valid V3ResponseForUseModel of the given model.
 */
function isV3Response(response) {
  return validateV3Response(response) === undefined;
}

/**
 * Validates whether the response is a valid V3ResponseForUseModel of the given model.
 * @param response The response to validate.
 * @param expectedModel The model that the response should be for.
 * @returns `undefined` if the response is valid, or an error message if not.
 */
function validateV3Response(response) {
  if (!response) return 'response is not an object.';
  if (typeof response !== 'object') return 'response is not an object.';
  if (!('success' in response)) return 'response does not have a `success` property.';
  if (!('result' in response)) return 'response does not have a `result` property.';
  const {
    success,
    result
  } = response;
  if (success !== true) return 'response.success is not true.';
  if (!Array.isArray(result)) return 'response.result is not an array.';
  for (let i = 0; i < result.length; i++) {
    const resultItem = result[i];
    if (!resultItem) return `response.result[${i}] is not an object.`;
    if (typeof resultItem !== 'object') return `response.result[${i}] is not an object.`;
    if (!('operation' in resultItem)) return `response.result[${i}] does not have an \`operation\` property.`;
    if (!('data' in resultItem)) return `response.result[${i}] does not have a \`data\` property.`;
    if (!('model' in resultItem)) return `response.result[${i}] does not have a \`model\` property.`;
    const {
      operation,
      data,
      model
    } = resultItem;
    if (typeof operation !== 'string') return `response.result[${i}] operation is not one of 'get', 'create', 'update', or 'delete'.`;
    if (!['get', 'create', 'update', 'delete'].includes(operation)) return `response.result[${i}] operation is not one of 'get', 'create', 'update', or 'delete'.`;
    if (!(0, _modelStore.isModelName)(model)) return `response.result[${i}].model is not a known model.`;
    if (!Array.isArray(data)) return `response.result[${i}].data is not an array.`;
    if (data.length > 0) {
      if (operation === 'delete') {
        if (typeof data[0] !== 'number') {
          return `response.result[${i}] is operation 'delete', but data[0] is not a number.`;
        }
      } else {
        let primaryKey = (0, _modelStore.getModelPrimaryKey)(model);
        if (!(primaryKey in data[0])) {
          return `response.result[${i}].data[0] does not have a \`${primaryKey}\` property, which is the primary key for model \`${model}\`.`;
        }
      }
    }

    // Going to stop here, but data should be an array of the expected model type.
  }

  return;
}